import { EVENT_SEVERITY, MESSAGE_MAX_CHARS } from './constants';
import { enqueueEvent, listPendingEvents, markEventState } from './eventQueue';
import { createSecurityRequest } from './http';

function newId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function createSecurityEventLogger({
  examId,
  candidateId,
  getSessionId,
  websocketService = null,
  request: requestProp,
  apiBaseUrl
}) {
  const request = requestProp || createSecurityRequest(apiBaseUrl);
  let flushing = false;

  async function send(event) {
    const res = await request(`/api/exams/${encodeURIComponent(examId)}/security-events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        eventId: event.eventId,
        candidateId,
        sessionId: event.sessionId,
        type: event.type,
        severity: event.severity,
        message: event.message,
        clientTimestamp: event.clientTimestamp,
        metadata: event.metadata || {}
      })
    });
    if (!res.ok && res.status !== 409 && res.status !== 202) {
      throw new Error(`security event HTTP ${res.status}`);
    }
    return res;
  }

  async function flushQueue() {
    if (flushing) return;
    flushing = true;
    try {
      const pending = await listPendingEvents();
      for (const row of pending) {
        try {
          await markEventState(row.eventId, 'SENDING');
          await send(row);
          await markEventState(row.eventId, 'SENT');
        } catch (_) {
          await markEventState(row.eventId, 'FAILED');
        }
      }
    } catch (_) {
      /* IndexedDB optional */
    } finally {
      flushing = false;
    }
  }

  async function logSecurityEvent({ type, severity, message, metadata = {} }) {
    const sessionId = getSessionId();
    const event = {
      eventId: newId(),
      examId,
      candidateId,
      sessionId,
      type,
      severity: severity || EVENT_SEVERITY[type] || 'info',
      timestamp: new Date().toISOString(),
      clientTimestamp: new Date().toISOString(),
      message: String(message || '').slice(0, MESSAGE_MAX_CHARS),
      metadata
    };

    if (websocketService && typeof websocketService.sendMessage === 'function') {
      try {
        websocketService.sendMessage('exam-security', {
          channel: 'exam-security',
          examId,
          candidateId,
          event: { type: event.type, severity: event.severity, timestamp: event.timestamp }
        });
      } catch (_) {
        /* optional live hint */
      }
    }

    try {
      await send(event);
      return event;
    } catch (err) {
      try {
        await enqueueEvent({ ...event, state: 'PENDING' });
      } catch (_) {
        /* queue optional */
      }
      return { ...event, queued: true, error: err && err.message };
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('online', flushQueue);
  }

  return {
    logSecurityEvent,
    flush: flushQueue,
    flushQueue
  };
}
